/**
 * BundleCard — presentational add-on bundle card.
 *
 * Shared by the add-ons storefront (`/addons`) and the dedicated bundles page
 * (`/bundles`) so the pricing/savings/buy UI lives in exactly one place. Stateful
 * concerns (pending state, toasts, reloading purchases) stay in the page; this
 * component only renders and calls `onBuy`.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Coins, Package, Gift } from 'lucide-react';

import Button from '../base/Button';
import type { AddonBundle } from '../../services/bundleService';
import { formatAmount } from '../../utils/numberFormat';

interface BundleCardProps {
  bundle: AddonBundle;
  /** SAR-per-coin rate, for deriving the coin price under a plan discount. */
  coinRate: number;
  /** Buyer's plan add-on discount (%), applied on top of the bundle's own discount. */
  addonDiscount: number;
  isAuthed: boolean;
  isPending: boolean;
  onBuy: (method: 'SAR' | 'COINS') => void;
}

export default function BundleCard({
  bundle,
  coinRate,
  addonDiscount,
  isAuthed,
  isPending,
  onBuy,
}: BundleCardProps) {
  const { t: tb, i18n } = useTranslation('bundles');
  const isRTL = i18n.language === 'ar';

  const localizedName = useCallback(
    (b: { name: string; nameAr?: string | null }) => (isRTL ? b.nameAr || b.name : b.name),
    [isRTL],
  );
  const localizedDescription = useCallback(
    (b: { description?: string | null; descriptionAr?: string | null }) =>
      isRTL ? b.descriptionAr || b.description : b.description,
    [isRTL],
  );

  // Apply the buyer's plan discount to a SAR price (matches the backend).
  const discounted = useCallback(
    (listSar: number): number => listSar - Math.floor((listSar * addonDiscount) / 100),
    [addonDiscount],
  );

  const pricing = bundle.pricing;
  // Plan discount applies on top of the bundle's own final price.
  const planFinalSar = pricing ? discounted(pricing.finalSar) : 0;
  // Show the "was" price when the bundle is discounted OR the plan discount lowers it.
  const hasDiscount = !!pricing && (pricing.discountSar > 0 || planFinalSar < pricing.baseSar);
  // Coin price scaled by the plan discount (mirrors the backend).
  const planPriceCoins =
    pricing && coinRate > 0 && addonDiscount > 0
      ? Math.ceil(planFinalSar / coinRate)
      : pricing?.priceCoins ?? 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-[var(--pv-color-accent-soft)] dark:border-[#004D40]/40 p-6 flex flex-col shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-[#004D40]" />
        </div>
        <h3 className="font-semibold text-gray-900 dark:text-white">{localizedName(bundle)}</h3>
      </div>

      {localizedDescription(bundle) && (
        <p className="text-sm text-gray-500 mb-4">{localizedDescription(bundle)}</p>
      )}

      {/* What's inside */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-500 mb-1.5">{tb('whatsInside')}</p>
        <ul className="space-y-1">
          {bundle.items.map((item) => (
            <li
              key={item.id ?? item.addonId}
              className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2"
            >
              <Package className="w-3.5 h-3.5 text-[#004D40] flex-shrink-0" />
              {item.addon
                ? tb('itemLine', { name: localizedName(item.addon), count: item.quantity })
                : tb('itemLine', { name: '', count: item.quantity })}
            </li>
          ))}
          {/* Every subscriber bundle includes access to the Special deals board. */}
          <li className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Package className="w-3.5 h-3.5 text-[#004D40] flex-shrink-0" />
            {tb('specialDealsIncluded', { defaultValue: 'Access to subscriber Special deals' })}
          </li>
        </ul>
      </div>

      {/* Pricing */}
      <div className="mt-auto pt-3 border-t dark:border-gray-700">
        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
          {hasDiscount && pricing && (
            <span className="text-sm text-gray-400 line-through">
              {tb('priceSar', { price: formatAmount(pricing.baseSar) })}
            </span>
          )}
          <span className="text-lg font-bold text-[#004D40]">
            {pricing ? tb('priceSar', { price: formatAmount(planFinalSar) }) : tb('free')}
          </span>
        </div>
        {hasDiscount && pricing && (
          <p className="text-xs font-medium text-green-600 mb-3">
            {tb('youSave', { amount: formatAmount(pricing.baseSar - planFinalSar) })}
          </p>
        )}

        {isAuthed ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => onBuy('SAR')} loading={isPending} disabled={isPending}>
              {tb('buy')}
            </Button>
            {pricing && planPriceCoins > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onBuy('COINS')}
                loading={isPending}
                disabled={isPending}
              >
                <Coins className="w-3.5 h-3.5" aria-hidden="true" />
                {tb('coinPrice', { count: planPriceCoins })}
              </Button>
            )}
          </div>
        ) : (
          <Link to="/signin">
            <Button size="sm" variant="outline">
              {tb('buy')}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
